// components/common/query/ReceitaFederalLookup.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Badge, Table, Alert, Spinner } from "flowbite-react";
import { HiOutlineEye, HiRefresh, HiExclamation } from "react-icons/hi";

/* ===== Utils locais (sem dependências externas) ===== */
const onlyDigits = (v = "") => String(v).replace(/\D+/g, "");
const maskCNPJ = (cnpj = "") =>
  onlyDigits(cnpj).replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/,
    "$1.$2.$3/$4-$5"
  ) || "—";

const formatDate = (iso) => {
  if (!iso || iso === "0000-00-00") return "—";
  // tenta YYYY-MM-DD, senão devolve como veio
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [_, y, mo, d] = m;
  try {
    const dt = new Date(`${y}-${mo}-${d}T00:00:00`);
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
    }).format(dt);
  } catch {
    return iso;
  }
};

const formatCEP = (cep = "") =>
  onlyDigits(cep).replace(/^(\d{5})(\d{3})$/, "$1-$2") || "—";

const formatMoneyBR = (v) => {
  if (v == null) return "—";
  // aceita "40000,00" ou number
  if (typeof v === "string") {
    const norm = v.replace(/\./g, "").replace(",", ".");
    const n = Number(norm);
    if (Number.isFinite(n)) {
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    }
  }
  if (typeof v === "number") {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
  return String(v);
};

const joinAddress = (obj = {}) => {
  const parts = [
    [obj.logradouro, obj.numero].filter(Boolean).join(", "),
    obj.complemento,
    obj.bairro,
    [obj.municipio, obj.uf].filter(Boolean).join(" - "),
    formatCEP(obj.cep),
  ].filter(Boolean);
  return parts.join(" • ");
};

/* ===== Componente principal ===== */
export default function ModalReceitaFederal({
  cnpj,
  buttonLabel = "Abrir dados da Receita Federal",
  size = "7xl",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const controllerRef = useRef(null);

  const cnpjDigits = useMemo(() => onlyDigits(cnpj), [cnpj]);

  const fetchData = async (force = false) => {
    if (!cnpjDigits || cnpjDigits.length !== 14) {
      setErr("CNPJ inválido ou ausente.");
      setData(null);
      setLoading(false);
      return;
    }
    if (data && !force) return; // já carregado
    setLoading(true);
    setErr(null);

    // cancela requisição anterior
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const ctl = new AbortController();
    controllerRef.current = ctl;

    try {
      const resp = await fetch(`https://api.opencnpj.org/${cnpjDigits}`, {
        method: "GET",
        signal: ctl.signal,
      });

      if (!resp.ok) {
        if (resp.status === 404) throw new Error("CNPJ não encontrado (404).");
        if (resp.status === 429)
          throw new Error(
            "Muitas requisições (429). Tente novamente em instantes."
          );
        throw new Error(`Erro ao consultar (${resp.status}).`);
      }

      const json = await resp.json();
      setData(json);
    } catch (e) {
      if (e.name !== "AbortError") {
        setErr(e.message || "Erro ao consultar os dados.");
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Abre o modal e busca (se ainda não buscou)
  const handleOpen = async () => {
    setOpen(true);
    setTimeout(() => fetchData(false), 0);
  };

  // Ao trocar o CNPJ, limpa dados (para evitar misturar respostas antigas)
  useEffect(() => {
    setData(null);
    setErr(null);
    setLoading(false);
    if (open) fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjDigits]);

  const payload = data || {}; // estrutura desconhecida -> protege acessos

  // Compatível com o JSON enviado no prompt do usuário
  const mapped = useMemo(() => {
    // tenta usar chaves do OpenCNPJ diretamente; se vier no seu formato, também mapeia
    const base = {
      cnpj: payload.cnpj || payload?.dadosCadastrais?.cnpj || "",
      razao_social:
        payload.razao_social || payload?.dadosCadastrais?.razaoSocial || "",
      nome_fantasia:
        payload.nome_fantasia || payload?.dadosCadastrais?.nomeFantasia || "",
      situacao_cadastral:
        payload.situacao_cadastral || payload?.dadosCadastrais?.situacao || "",
      data_situacao_cadastral:
        payload.data_situacao_cadastral ||
        payload?.dadosCadastrais?.dataSituacao ||
        "",
      natureza_juridica:
        payload.natureza_juridica ||
        payload?.dadosCadastrais?.naturezaJuridica ||
        "",
      data_inicio_atividade:
        payload.data_inicio_atividade ||
        payload?.dadosCadastrais?.dataFundacao ||
        "",
      porte_empresa:
        payload.porte_empresa || payload?.dadosCadastrais?.porte || "",
      capital_social:
        payload.capital_social || payload?.dadosCadastrais?.capitalSocial || "",
      cnae_principal:
        payload.cnae_principal ||
        payload?.dadosCadastrais?.cnaePrincipal ||
        payload?.dadosCadastrais?.ramoAtividade ||
        "",
      cnaes_secundarios:
        payload.cnaes_secundarios ||
        payload?.dadosCadastrais?.cnaesSecundarios ||
        [],
      logradouro:
        payload.logradouro || payload?.dadosCadastrais?.endereco || "",
      numero: payload.numero || payload?.dadosCadastrais?.numero || "",
      complemento:
        payload.complemento || payload?.dadosCadastrais?.complemento || "",
      bairro: payload.bairro || payload?.dadosCadastrais?.bairro || "",
      cep: payload.cep || payload?.dadosCadastrais?.cep || "",
      municipio: payload.municipio || payload?.dadosCadastrais?.cidade || "",
      uf: payload.uf || payload?.dadosCadastrais?.uf || "",
      email: payload.email || payload?.dadosCadastrais?.email || "",
      telefones: payload.telefones || payload?.dadosCadastrais?.telefones || [],
      QSA: payload.QSA || payload?.dadosCadastrais?.socios || [],
      opcao_simples:
        payload.opcao_simples || payload?.dadosCadastrais?.opcaoSimples || "",
      data_opcao_simples:
        payload.data_opcao_simples ||
        payload?.dadosCadastrais?.dataOpcaoSimples ||
        "",
      opcao_mei: payload.opcao_mei || payload?.dadosCadastrais?.opcaoMEI || "",
      data_opcao_mei:
        payload.data_opcao_mei || payload?.dadosCadastrais?.dataOpcaoMEI || "",
      matriz_filial:
        payload.matriz_filial || payload?.dadosCadastrais?.matrizFilial || "",
    };
    return base;
  }, [payload]);

  return (
    <div className={className}>
      <Button
        className="mt-1"
        color="light"
        size="xs"
        onClick={handleOpen}
        disabled={!cnpjDigits || cnpjDigits.length !== 14}
        title={
          !cnpjDigits || cnpjDigits.length !== 14
            ? "Informe um CNPJ válido (14 dígitos)"
            : ""
        }
      >
        <div className="flex items-center">
          <HiOutlineEye className="mr-1" />
          <span>{buttonLabel}</span>
        </div>
      </Button>

      <Modal show={open} size={size} onClose={() => setOpen(false)}>
        <Modal.Header>
          Dados da Receita Federal —{" "}
          <span className="ml-1 font-normal">{maskCNPJ(cnpjDigits)}</span>
        </Modal.Header>
        <Modal.Body>
          {/* Barra de ações/estado */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge color="gray" className="w-fit">
                Consulta em tempo real
              </Badge>
              {loading && (
                <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <Spinner size="sm" /> Buscando…
                </span>
              )}
            </div>
            <Button color="light" size="xs" onClick={() => fetchData(true)}>
              <HiRefresh className="mr-2" />
              Recarregar
            </Button>
          </div>

          {/* Erro */}
          {err && (
            <Alert color="failure" className="mb-4">
              <div className="flex items-center gap-2">
                <HiExclamation className="text-lg" />
                <span>{err}</span>
              </div>
            </Alert>
          )}

          {/* Loading inicial */}
          {!err && loading && !data && (
            <div className="border rounded-lg p-6 text-sm text-gray-600">
              Carregando informações do CNPJ…
            </div>
          )}

          {/* Conteúdo */}
          {!loading && !err && data && (
            <div className="space-y-6">
              {/* Visão geral */}
              <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 border rounded-xl bg-white">
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Razão Social
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.razao_social || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Nome Fantasia
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.nome_fantasia || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">CNPJ</div>
                  <div className="font-medium text-gray-900">
                    {maskCNPJ(mapped.cnpj)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500 flex">
                    Situação Cadastral
                  </div>
                  <div className="font-medium mt-1">
                    <Badge
                      color={
                        (mapped.situacao_cadastral || "")
                          .toLowerCase()
                          .includes("ativa")
                          ? "success"
                          : "failure"
                      }
                      className="w-fit"
                    >
                      {mapped.situacao_cadastral || "—"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Natureza Jurídica
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.natureza_juridica || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Abertura
                  </div>
                  <div className="font-medium text-gray-900">
                    {formatDate(mapped.data_inicio_atividade)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">Porte</div>
                  <div className="font-medium text-gray-900">
                    {mapped.porte_empresa || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Capital Social
                  </div>
                  <div className="font-medium text-gray-900">
                    {formatMoneyBR(mapped.capital_social)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Matriz/Filial
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.matriz_filial || "—"}
                  </div>
                </div>
              </section>

              {/* Endereço & Contatos */}
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="p-4 border rounded-xl bg-white">
                  <div className="text-sm font-semibold mb-2">Endereço</div>
                  <div className="text-gray-800">{joinAddress(mapped)}</div>
                </div>
                <div className="p-4 border rounded-xl bg-white">
                  <div className="text-sm font-semibold mb-2">Contato</div>
                  <div className="text-gray-800">
                    <div>
                      <span className="text-gray-500">E-mail:</span>{" "}
                      {mapped.email || "—"}
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-500">Telefones:</span>{" "}
                      {Array.isArray(mapped.telefones) &&
                      mapped.telefones.length > 0 ? (
                        <span className="inline-flex flex-wrap gap-2">
                          {mapped.telefones.map((t, i) => (
                            <Badge key={i} color="gray" className="w-fit">
                              {(t?.ddd ? `(${t.ddd}) ` : "") +
                                (t?.numero || "—")}
                              {t?.is_fax ? " • fax" : ""}
                            </Badge>
                          ))}
                        </span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* CNAEs */}
              <section className="p-4 border rounded-xl bg-white">
                <div className="text-sm font-semibold mb-2">
                  Atividade Econômica (CNAE)
                </div>
                <div className="mb-2 text-gray-800">
                  <span className="text-gray-500">Principal:</span>{" "}
                  <Badge color="info" className="w-fit">
                    {mapped.cnae_principal || "—"}
                  </Badge>
                </div>
                <div className="text-gray-800">
                  <span className="text-gray-500">Secundários:</span>{" "}
                  {Array.isArray(mapped.cnaes_secundarios) &&
                  mapped.cnaes_secundarios.length > 0 ? (
                    <span className="inline-flex flex-wrap gap-2 mt-1">
                      {mapped.cnaes_secundarios.map((c, i) => (
                        <Badge key={i} color="gray" className="w-fit">
                          {c}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
              </section>

              {/* Simples/MEI */}
              <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-xl bg-white">
                  <div className="text-xs uppercase text-gray-500">
                    Optante Simples
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.opcao_simples === "S"
                      ? "Sim"
                      : mapped.opcao_simples === "N"
                      ? "Não"
                      : "—"}
                    {mapped.data_opcao_simples && (
                      <span className="ml-2 text-xs text-gray-500">
                        {formatDate(mapped.data_opcao_simples)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 border rounded-xl bg-white">
                  <div className="text-xs uppercase text-gray-500">
                    Optante MEI
                  </div>
                  <div className="font-medium text-gray-900">
                    {mapped.opcao_mei === "S"
                      ? "Sim"
                      : mapped.opcao_mei === "N"
                      ? "Não"
                      : "—"}
                    {mapped.data_opcao_mei && (
                      <span className="ml-2 text-xs text-gray-500">
                        {formatDate(mapped.data_opcao_mei)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-4 border rounded-xl bg-white">
                  <div className="text-xs uppercase text-gray-500">
                    Situação
                  </div>
                  <div className="font-medium">
                    <Badge
                      color={
                        (mapped.situacao_cadastral || "")
                          .toLowerCase()
                          .includes("ativa")
                          ? "success"
                          : "failure"
                      }
                      className="w-fit"
                    >
                      {mapped.situacao_cadastral || "—"}
                    </Badge>
                  </div>
                </div>
              </section>

              {/* QSA */}
              <section className="p-4 border rounded-xl bg-white">
                <div className="text-sm font-semibold mb-3">
                  Quadro Societário (QSA)
                </div>
                {Array.isArray(mapped.QSA) && mapped.QSA.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Head>
                        <Table.HeadCell>Nome</Table.HeadCell>
                        <Table.HeadCell>Qualificação</Table.HeadCell>
                        <Table.HeadCell>Identificador</Table.HeadCell>
                        <Table.HeadCell>Entrada</Table.HeadCell>
                        <Table.HeadCell>Faixa Etária</Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {mapped.QSA.map((s, i) => (
                          <Table.Row key={i}>
                            <Table.Cell className="font-medium">
                              {s?.nome_socio || s?.nome || "—"}
                            </Table.Cell>
                            <Table.Cell>
                              {s?.qualificacao_socio || s?.qualificacao || "—"}
                            </Table.Cell>
                            <Table.Cell>
                              {s?.identificador_socio || s?.tipo || "—"}
                            </Table.Cell>
                            <Table.Cell>
                              {formatDate(
                                s?.data_entrada_sociedade || s?.entrada || ""
                              )}
                            </Table.Cell>
                            <Table.Cell>{s?.faixa_etaria || "—"}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    Sem registros de QSA.
                  </div>
                )}
              </section>
            </div>
          )}

          {/* Nada para mostrar (ex.: CNPJ inválido) */}
          {!loading && !err && !data && (
            <div className="text-sm text-gray-600 border rounded-lg p-4">
              Informe um CNPJ válido para consultar.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
